import { Injectable, Type } from '@angular/core';
import { BaseApiService } from '@kinet-open-network/client/core/api-client';
import { mapToModel } from '@kinet-open-network/client/shared/data-access/model';
import {
  DeviceState,
  ICustomerDevice,
} from '@kinet-open-network/shared/data-access/model';
import { formatISO } from 'date-fns';
import { MoveDeviceData } from '../models/api/move-device.model';
import {
  encodeInvoicingPeriod,
  SetInvoicingPeriod,
} from '../models/api/set-invoicing-period.model';
import { CustomerDevice } from '../models/customer-device.model';

@Injectable({
  providedIn: 'root',
})
export class CustomerDevicesService extends BaseApiService<CustomerDevice> {
  override model: Type<CustomerDevice> = CustomerDevice;

  get endpoint(): string {
    return '/customer-devices';
  }

  returnDevice(id: string, returnDate?: Date | null) {
    return this.http
      .put(`${this.endpoint}/${id}/return`, {
        returnDate: returnDate
          ? formatISO(returnDate, { representation: 'date' })
          : null,
      })
      .pipe(mapToModel(this.model));
  }

  markFinePaid(id: string, finePaidAt?: Date | null) {
    return this.http
      .put(`${this.endpoint}/${id}/fine-paid`, {
        finePaidAt: finePaidAt
          ? formatISO(finePaidAt, { representation: 'date' })
          : null,
      })
      .pipe(mapToModel(this.model));
  }

  setDeliveryDate(id: string, deliveryDate: Date | null) {
    return this.http
      .put<ICustomerDevice>(`${this.endpoint}/${id}/delivery-date`, {
        deliveryDate: deliveryDate
          ? formatISO(deliveryDate, { representation: 'date' })
          : null,
      })
      .pipe(mapToModel(this.model));
  }

  updateState(
    id: string,
    data: Partial<{
      state: DeviceState;
      deliveryDate: Date | null;
      returnDate: Date | null;
    }>,
  ) {
    return this.http
      .put(`${this.endpoint}/${id}/state`, {
        ...data,
        deliveryDate: data.deliveryDate
          ? formatISO(data.deliveryDate, { representation: 'date' })
          : null,
        returnDate: data.returnDate
          ? formatISO(data.returnDate, { representation: 'date' })
          : null,
      })
      .pipe(mapToModel(this.model));
  }

  changeAp(id: string, data: MoveDeviceData) {
    return this.http
      .put<ICustomerDevice>(`${this.endpoint}/${id}/change-ap`, data)
      .pipe(mapToModel(this.model));
  }

  setInvoicingPeriod(id: string, data: SetInvoicingPeriod) {
    return this.http
      .put<ICustomerDevice>(
        `${this.endpoint}/${id}/invoicing/period`,
        encodeInvoicingPeriod(data),
      )
      .pipe(mapToModel(this.model));
  }

  setInvoiceState(id: string, invoiceDate: Date | null) {
    return this.http
      .put<ICustomerDevice>(`${this.endpoint}/${id}/invoicing/state`, {
        invoiceDate: invoiceDate
          ? formatISO(invoiceDate, { representation: 'date' })
          : null,
      })
      .pipe(mapToModel(this.model));
  }
}
